import React, { useState, useEffect, useCallback, FC, useRef, useMemo, CSSProperties } from 'react';
import { useApi, IApi, useAuth, useDeviceConfig, useApiLoader } from 'utils/API';
import { useTranslations } from 'utils/language';
import { Tab, UncontrolledTabs } from 'components/uncontrolled_tabs';
import Model, { useCustomTabs } from 'components/objects/model';
import Location from 'components/objects/location';
import PropertyList from 'components/property_list';
import { IAuthenticatedUser, IBatch, IBinLocation, IDefaultBinLocation, IItem, IItemBinLocation, IItemMovementHistoryEntry, IItemOpenDocuments, ISerialNumber, ManagedByValues } from 'interfaces';
import Serial from '../serial';
import Batch from '../batch';
import Bool from 'components/Bool';
import { compiledUserCompoundItemBarcodeParser, compoundItemBarcodeParser, useConfig } from 'utils/Config';
import Translation from 'utils/Language/Translation';
import Warehouse from '../warehouse';
import { Badge, Button, ButtonGroup, Col, Container, Input, InputGroup, InputGroupAddon, Row, Spinner } from 'reactstrap';
import { addNotification, useNotifications } from 'utils/NotificationManager';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import SimpleModal from 'components/SimpleModal';
import { beTableAsComponent } from 'components/BeTable';
import ProcessingButton from 'components/ProcessingButton';
import { EBarcodeFields, IIItemDao$ICompanion$ISetDefaultBinLocation } from 'interfaces/erp-interfaces';
import { askPrinterIfNotSet, choiceList, confirm, prompt, promptAutocomplete } from 'utils/Prompts';
import CDate, { DATE, DATE_TIME } from 'components/Date';
import { hasBatch, IContentsLocationTreeLine, isBatch, isSerial, toLocationTree } from 'component_utils/itemLocationTree';
import ExpandedIndicator from 'components/ExpandedIndicator';
import { getLeftPaddingForLevel, tableHighlightColor } from 'styling/stylingVars';
import ImageNotFound from './404.png'
import { cast, distinctBy, run, saveBase64UrlEncode, sleep, useEffectAsync, useRerender } from 'component_utils/utils';
import If from 'components/If';
import { RiNumber1 } from 'react-icons/ri';
import { FaChartLine, FaCopy, FaEdit, FaPlus, FaTimes } from 'react-icons/fa';
import serialSfx from 'audio/serialSound.mp3';
import { entries, first, groupBy, last, max, min, sortBy, sumBy, values } from 'lodash';
import RoleProtected from 'components/RoleProtected';
import { FloatType, FormInput, FormObject, FormSelect, FormState, FormToggle, Labeled } from 'component_utils/FormUtils';
import PictureBox, { PictureBoxRef } from 'components/PictureBox';
import { ProductionOrderCreator } from './StartProductionForItem';
import { PdfPreview } from 'components/PdfViewer';
import moment from 'moment';
import EChartsReact from 'echarts-for-react';
import { useSpecializations } from 'utils/SpecializationProvider';
import { MdClear } from 'react-icons/md';

interface ItemImageProps {
  obj: IItem | null;
  imgProps: any;
  openOnClick?: boolean;
}
export const ItemImage = ({ obj, openOnClick, imgProps = {} }: ItemImageProps) => {
  if (!obj) {
    return null;
  }

  const onErr: React.ReactEventHandler<HTMLImageElement> = (e) => {
    if (e.currentTarget.src !== ImageNotFound) {
      console.log('Image could not be loaded, falling back to placeholder');
      e.currentTarget.src = ImageNotFound;
    }
  };

  const onLoad: React.ReactEventHandler<HTMLImageElement> = (e) => {
    if (e.currentTarget.src !== ImageNotFound && e.currentTarget.width === 1 && e.currentTarget.height === 1) {
      console.log('Image was a 1x1 image, consider it not available');
      e.currentTarget.src = ImageNotFound;
    }
  };

  const imageSource = obj?.imagePath || `/api/v1/item/${saveBase64UrlEncode(obj.code)}/image`;
  return (
    <SimpleModal
      fullscreen
      title={<Translation name="T.misc.itemImage" />}
      trigger={(open) => (
        <img alt="item" src={imageSource} onLoad={onLoad} onError={onErr} onClick={openOnClick && open} {...imgProps} />
      )}
    >
      <img
        alt="item"
        src={imageSource}
        style={{
          height: '100%',
          width: '100%',
          objectFit: 'contain',
        }}
        onLoad={onLoad}
        onError={onErr}
      />
    </SimpleModal>
  );
};

const ItemImageSetter: FC<{ itemCode: string; onImageChanged: (imageUrl: string) => any; }> = ({ itemCode, onImageChanged }) => {
  const pictureBoxRef = useRef<PictureBoxRef>();
  const api = useApi()

  return (
    <>
      <PictureBox
        hideCurrentFilesOverview
        closeAfterOnPicture
        ref={pictureBoxRef}
        files={[]}
        onAddFiles={async (files) => {
          const { blob, extension } = files[0]
          const fileName = itemCode + '.' + extension
          const fileUrl = '/userdata/static/item_images/' + fileName

          await api.fileManagement.uploadFile({
            path: ['item_images', fileName],
            contents: blob
          })
          await api.item.updateItem(itemCode, {
            imagePath: fileUrl
          })
          try {
            await fetch(fileUrl, {cache: "reload"})
          } catch (e) {}
          onImageChanged(fileUrl)
        }}
        onRemoveFile={() => {}}
      />
      <ProcessingButton 
        block
        color="primary"      
        onClick={() => pictureBoxRef.current.open()}
      >
        <Translation name='T.objects.item.setPicture' />
      </ProcessingButton>
    </>
  )
}

const UpdateManagedBy: FC<{ item: IItem }> = ({ item }) => {
  const [state, setState] = useState({} as any)
  const api = useApi()
  return (
    <SimpleModal
      title={<Translation name="T.objects.item.actions.updateManagedBy"/>}
      onOpen={() => {
        setState({
          managedBy: item.managedBy,
          newBatchName: "BE_CONV",
          newSerialPrefix: "BE_CONV_"
        })
      }}
      trigger={open => (
        <ProcessingButton onClick={open} color='primary' block>
          <Translation name="T.objects.item.actions.updateManagedBy"/>
        </ProcessingButton>  
      )}
      buttons={(close) => [
        <ProcessingButton color="secondary" onClick={close} data-testid="close-pdf-processor" key="close">
          <Translation name="T.misc.cancel" />
        </ProcessingButton>,
        <ProcessingButton
          color="primary"
          onClick={async () => {
            await api.item.setManagedBy(item.code, state.managedBy, state.newBatchName, state.newSerialPrefix)
            addNotification('success', <Translation name="T.objects.item.managedByConv.succesfull"/>)
            close();
          }}
          data-testid="parse-pdf-document"
          key="parse"
        >
          <Translation name="T.misc.ok" />
        </ProcessingButton>,
      ]}
    >
      <FormState state={state} setState={setState} disableFormWrapper>
        <Labeled title={<Translation name="T.objects.item.managedByConv.managedBy"/>}>
          <FormSelect
            path='managedBy'
            options={ManagedByValues.map(it => ({ label: it, value: it }))}
          />
        </Labeled>

        <If cond={['BATCH_NUMBER', 'SERIAL_NUMBER'].includes(state.managedBy)}>
          <Labeled title={<Translation name="T.objects.item.managedByConv.newBatchName"/>}>
            <FormInput path='newBatchName'/>
          </Labeled>
        </If>

        <If cond={['SERIAL_NUMBER'].includes(state.managedBy)}>
          <Labeled title={<Translation name="T.objects.item.managedByConv.newSerialPrefix"/>}>
            <FormInput path='newSerialPrefix'/>
          </Labeled>
        </If>
      </FormState>
    </SimpleModal>
  )
}

const LocationsTable = beTableAsComponent<IContentsLocationTreeLine>({
  tableId: 'itemLocations',
  subRowsKeys: ['batches', 'serials'],
  showRowsWithoutChildren: true,
  columns: [
    {
      Header: <Translation name="T.objects.binLocation.binCode" />,
      value: (row) => row.location.visualCode,
      Cell: ({ row, level, isExpanded }) => {
        let inner = null;
        let hasChildren = false;
        if (level === 0) {
          inner = <Location obj={row.location} inline={true} />;
          hasChildren = row.batches.length > 0;
        } else if (level === 1) {
          const batch = row as unknown as IContentsLocationTreeLine['batches'][0];
          inner = batch.batch ? <Batch inline obj={batch.batch} /> : batch.batchCode;
          hasChildren = batch.serials.length > 0;
        } else if (level === 2) {
          const serial = row as unknown as IContentsLocationTreeLine['batches'][0]['serials'][0];
          inner = <Serial inline obj={serial.serial} />;
          hasChildren = false;
        }

        return (
          <div style={{ paddingLeft: getLeftPaddingForLevel(level) }}>
            <ExpandedIndicator isExpanded={isExpanded} disabled={!hasChildren} />
            {inner}
          </div>
        );
      },
      filterable: true,
      expander: true,
      width: 100,
      Footer: ({ rows }) => {
        return <Translation name="T.objects.contents.uniqueLocations" params={{ number: rows.length }}/>
      }
    },
    {
      Header: <Translation name="T.objects.contents.onHandQuantity" />,
      value: row => row.onHandQty,
      className: 'text-right',
      Footer: ({ rows }) => {
        return sumBy(rows, it => it.onHandQty)
      }
    },
    {
      Header: <Translation name="T.objects.contents.freeToMoveQuantity" />,
      value: row => row.freeToMoveQty,
      className: 'text-right',
      hideBelow: 'sm',
    },
    {
      Header: <Translation name="T.objects.contents.releasedQuantity" />,
      value: row => row.releasedQty,
      className: 'text-right',
      hideBelow: 'sm',
    },
    {
      Header: <Translation name="T.objects.contents.pickedQuantity" />,
      value: row => row.pickQty,
      className: 'text-right',
      hideBelow: 'sm',
    },
  ],
});

interface BatchesTableRow {
  batch: IBatch;
  onHandQuantity: number;
  locations: {
    location: IBinLocation;
    onHandQuantity: number;
  }[];
}
const BatchesTable = beTableAsComponent<BatchesTableRow, { highlightedBatch: IBatch }>({
  tableId: 'itemActiveBatches',
  subRowsKeys: ['locations'],
  getRowStyle(args) {
    const style = {} as CSSProperties
    if (args.level === 0 && args.row.batch.code === args.props.highlightedBatch?.code) {
      style.backgroundColor = tableHighlightColor;
    }
    return style
  },
  columns: [
    {
      Header: <Translation name="T.objects.contents.batch"/>,
      Cell: ({ row, level, isExpanded, isListLayout }) => {
        let inner = null
        let hasChildren = null

        if (level === 0) {
          hasChildren = true
          inner = <Batch inline obj={row.batch}/>
        } else if (level === 1) {
          hasChildren = false
          inner = <Location inline obj={cast<BatchesTableRow['locations'][0]>(row).location} />
        }

        return (
          <div 
            className='d-flex flex-row w-100'
            style={{ paddingLeft: isListLayout ? undefined : getLeftPaddingForLevel(level) }}  
          >
            <ExpandedIndicator disabled={!hasChildren} isExpanded={isExpanded}/>
            <div className='d-inline-block flex-fill'>
              {inner}
            </div>
          </div>
        )
      },
      expander: true,
      value: (row, level) => level === 0 ? row.batch.visualCode : '',
      filterable: true,
      Footer: ({ rows }) => {
        return <Translation name="T.objects.contents.uniqueBatches" params={{ number: rows.length }}/>
      }
    },
    {
      Header: <Translation name="T.objects.contents.onHandQuantity" />,
      value: row => row.onHandQuantity,
      className: 'text-right',
      width: 10,
      Footer: ({ rows }) => {
        return sumBy(rows, it => it.onHandQuantity)
      }
    },
  ]
})

interface SerialsTableRow {
  serial: ISerialNumber;
  location: IBinLocation;
}
const SerialsTable = beTableAsComponent<SerialsTableRow, { highlightedSerial: ISerialNumber }>({
  tableId: 'itemActiveSerials',
  getRowStyle(args) {
    const style = {} as CSSProperties
    if (args.row.serial.code === args.props.highlightedSerial?.code) {
      style.backgroundColor = tableHighlightColor;
    }
    return style
  },
  columns: [
    {
      Header: <Translation name="T.objects.contents.serialNumber"/>,
      Cell: ({ row }) => <Serial inline obj={row.serial}/>,
      value: row => row.serial.visualCode,
      filterable: true,
      Footer: ({ rows }) => {
        return <Translation name="T.objects.contents.uniqueSerials" params={{ number: rows.length }}/>
      }
    },
    {
      Header: <Translation name="T.objects.binLocation.binCode"/>,
      Cell: ({ row }) => <Location inline obj={row.location}/>,
      value: row => row.location.visualCode,
      filterable: true,
    },
  ]
})

interface ExtraRenderProps {
  loadDefaultLocations: any;
  item: IItem;
}

interface ShowColumnProps {
  auth: IAuthenticatedUser;
}

const DefaultLocationsTable = beTableAsComponent<IDefaultBinLocation, ExtraRenderProps, ShowColumnProps>({
  tableId: 'defaultLocations',
  columns: [
    {
      Header: <Translation name="T.misc.warehouse" />,
      Cell: ({ row }) => {
        return <Warehouse inline obj={row.whs} />;
      },
    },
    {
      Header: <Translation name="T.misc.location" />,
      Cell: ({ row }) => {
        return <Location inline obj={row.binLocation} />;
      },
    },
    {
      Header: <Translation name="T.misc.enforced" />,
      hideBelow: 'sm',
      width: 20,
      className: 'text-center',
      value: (row) => (row.enforced ? 'Y' : 'N'),
      Cell: ({ row }) => {
        return <FontAwesomeIcon icon={row.enforced ? faLock : faLockOpen} />;
      },
    },
    {
      Header: <Translation name="T.misc.minStock" />,
      hideBelow: 'sm',
      width: 20,
      className: 'text-right',
      value: (row) => row.minStock,
    },
    {
      Header: <Translation name="T.misc.maxStock" />,
      hideBelow: 'sm',
      width: 20,
      className: 'text-right',
      value: (row) => row.maxStock,
    },
    {
      Header: <Translation name="T.misc.edit" />,
      width: 20,
      className: 'text-center',
      show: ({ auth }) => auth.expandedRoles.includes('defaultItemBinLocationEdit'),
      Cell: ({ row, loadDefaultLocations, item }) => {
        const notifications = useNotifications();
        const api = useApi();
        const [state, setState] = useState({} as (IIItemDao$ICompanion$ISetDefaultBinLocation & { locationCode: string }))
        return (
          <SimpleModal
            title={<Translation name="T.misc.editDefaultItemLocations" params={{ whs: row.whs.name }} />}
            fullscreen
            trigger={(show) => (
              <Button className="py-0" onClick={show} color="info">
                <Translation name="T.misc.edit" />
              </Button>
            )}
            onOpen={() => {
              setState({
                locationCode: row.binLocation?.code,
                location: row.binLocation,
                enforced: row.enforced,
                minStock: row.minStock,
                maxStock: row.maxStock,
              });
            }}
            buttons={(close) => [
              <ProcessingButton
                color="primary"
                onClick={async () => {
                  await api.item.setDefaultLocation(item.code, row.whs.code, state);
                  notifications.add('success', <Translation name="T.misc.success" />);
                  await loadDefaultLocations();
                  close();
                }}
                data-testid="btn-save"
              >
                <Translation name="T.misc.save" />
              </ProcessingButton>,
              <ProcessingButton color="secondary" onClick={close} data-testid="btn-close">
                <Translation name="T.misc.close" />
              </ProcessingButton>,
            ]}
          >
            <Container fluid>
              <FormState
                state={state}
                setState={setState}
                disableFormWrapper
              >
                <Labeled title={<Translation name="T.objects.defaultItemLocation.location" />}>
                  <FormObject<IBinLocation>
                    path="locationCode"
                    objectPath='location'
                    autocomplete={api.binLocation.findAutocomplete}
                    find={api.binLocation.findBinLocation}
                    getCode={useCallback(it => it.code, [])}
                    getEditValue={useCallback(it => it.visualCode, [])}
                    render={useCallback((it) => <Location inline obj={it} />, [])}
                  />
                </Labeled>

                <Labeled title={<Translation name="T.objects.defaultItemLocation.enforced" />}>
                  <FormToggle path="enforced" />
                </Labeled>

                <Labeled title={<Translation name="T.objects.defaultItemLocation.minStock" />}>
                  <FormInput
                    path="minStock"
                    typed={FloatType}
                  />
                </Labeled>

                <Labeled title={<Translation name="T.objects.defaultItemLocation.maxStock" />}>
                  <FormInput
                    path="maxStock"
                    typed={FloatType}
                  />
                </Labeled>
              </FormState>
            </Container>
          </SimpleModal>
        );
      },
    },
  ],
});

interface CIItemOpenDocuments extends IItemOpenDocuments {
  atDate: Date;
}

const OpenDocumentsChart: FC<{ item: IItem, openDocuments: IItemOpenDocuments[] }> = ({ item, openDocuments}) => {
  const api = useApi();

  const [state, setState] = useState<{
    items: IItem[],
    currentStock: {[k: string]: number},
    stockFlows: {[k: string]: CIItemOpenDocuments[]},
  }>({ items: [], currentStock: {}, stockFlows: {} })

  const loadForItem = useCallback(
    async (item: IItem, _openDocuments: IItemOpenDocuments[]) => {
      const openDocuments = _openDocuments || await api.item.getOpenDocuments(item.code)
      const currentStock = sumBy(await api.item.getItemLocations(item.code), it => it.onHandQty)
      const stockFlow = openDocuments.map(it => ({
        ...it,
        atDate: moment(it.shipDate).toDate()
      }))
  
      setState(old => ({
        items: distinctBy([...old.items, item], it => it.code),
        stockFlows: {
          ...old.stockFlows,
          [item.code]: stockFlow,
        },
        currentStock: {
          ...old.currentStock,
          [item.code]: currentStock
        }
      }))  
    },
    [setState, api]
  )

  useEffectAsync(async () => {
    loadForItem(item, openDocuments)
  }, [item, openDocuments])

  const option = useMemo(() => {
    const firstDate = min(state.items.map(it => it.code).flatMap(c => state.stockFlows[c].map(it => it.atDate)))
    const latestDate = max(state.items.map(it => it.code).flatMap(c => state.stockFlows[c].map(it => it.atDate)))
    
    const dates: Date[] = [];
    let currDate = moment(firstDate).startOf('day');
    let lastDate = moment(latestDate).startOf('day');
    dates.push(currDate.toDate());
    while(currDate.add(1, 'days').diff(lastDate) < 0) {
      dates.push(currDate.toDate());
    }
    dates.push(currDate.toDate());

    return {
      title: {
        left: 'center',
        text: 'Predicted stock levels based on open documents'
      },
      dataZoom: [
        {
          type: 'slider',
          filterMode: 'none',
        },
        {
          type: 'inside',
          filterMode: 'none',
        }
      ],    
      tooltip: {
        trigger: 'axis',
        type: 'item',
        formatter: function (params: any[]) {
          //The datetime where the axisPointer is
          var date = new Date(params[0].name);
          return (
            '<b>' + 
            date.getDate() +
            '/' +
            (date.getMonth() + 1) +
            '/' +
            date.getFullYear() +
            '<b/><br/>' +
            params.map(it => {
              return it.marker + ' ' + it.seriesName + ': ' + it.value[1] + (it.value[2] ? '<br/>' + it.value[2] : '')
            }).join("<br/>")
          );
        },
        axisPointer: {
          animation: false
        }
      },
      xAxis: {
        type: 'time',
        splitLine: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        boundaryGap: [0, '100%'],
        splitLine: {
          show: false
        }
      },
      series: state.items.map(item => {
        const totalStock = state.currentStock[item.code] || 0
        let currentStock = totalStock
        const flow = (state.stockFlows[item.code] || []).filter(it => it.shipDate !== null && it.openQuantity !== 0)
        
        const groupedByDate = sortBy(entries(groupBy(flow, it => it.shipDate)), it => it[0])

        const datapoints = groupedByDate.map(([, events]) => {
          const at = events[0].atDate
          let add = 0;
          for (const ev of events) {
            if (ev.direction === 'IN') {
              add += ev.openQuantity
            }
            if (ev.direction === 'OUT') {
              add -= ev.openQuantity
            }  
          }
          currentStock += add;
          return {
            name: at.toString(),
            value: [
              [at.getFullYear(), at.getMonth() + 1, at.getDate()].join('/'),
              currentStock,
              events.map(it => {
                return `${it.type} ${it.visualCode}: ${it.openQuantity}`
              }).join("<br/>")
            ]
          }
        })

        if (first(datapoints)?.name !== firstDate.toString()) {
          datapoints.unshift({
            name: firstDate.toString(),
            value: [
              [firstDate.getFullYear(), firstDate.getMonth() + 1, firstDate.getDate()].join('/'),
              totalStock,
              ''
            ]
          })
        }
        if (last(datapoints)?.name !== latestDate.toString()) {
          datapoints.push({
            name: latestDate.toString(),
            value: [
              [latestDate.getFullYear(), latestDate.getMonth() + 1, latestDate.getDate()].join('/'),
              currentStock,
              ''
            ]
          })
        }
        
        
        return {
          name: item.visualCode,
          type: 'line',
          data: datapoints
        }
      })
    };
  }, [state])

  return (
    <div className="flex-container">
      <div>
        {state.items.map(it => (
          <Badge key={it.code} color="primary" className='mr-2' pill>
            <Item inline obj={it}/>
            {' '}
            {it.code !== item.code && (
              <FaTimes onClick={() => setState(old => ({ ...old, items: old.items.filter(i => i.code !== it.code)}))}/>
            )}
          </Badge>
        ))}

        <Badge color="primary" pill onClick={async () => {
          const input = await promptAutocomplete(<Translation name="T.hints.item"/>, '', api.item.findAutocomplete)
          if (!input) return;

          const compound = await compoundItemBarcodeParser(input, codes => api.item.findMultipleItem(codes))
          const { item } = compound
          if (!item) {
            throw addNotification('danger', <Translation name="T.errors.itemIdentification.unknownItemForCode" params={{ code: input }} />);
          }

          loadForItem(item, null)
        }}>
          <FaPlus/>
        </Badge>
      </div>

      <div className='nested-flex-container overflow-hidden'>
        <EChartsReact className="w-100 h-100" option={option} />
      </div>
    </div>
  );
};

const OpenDocumentsTable = beTableAsComponent<IItemOpenDocuments, { item: IItem }>({
  tableId: 'itemOrdersTable',
  columns: [
    {
      Header: ({ rows, item }) => (
        <>
          <Translation name="T.misc.document" />
          {' '}
          <SimpleModal
            fullscreen
            title={''}
            trigger={open => (
              <Button className='py-0' disabled={!rows || rows.length === 0} onClick={open}>
                <FaChartLine/>
              </Button>
            )}
          >
            <OpenDocumentsChart item={item} openDocuments={rows}/>
          </SimpleModal>
        </>
      ),
      value: (row) => `${row.type} ${row.visualCode}`,
    },
    {
      Header: () => <Translation name="T.misc.cardName" />,
      value: (row) => row.businessPartnerCode ? `${row.businessPartnerCode} - ${row.businessPartnerName}` : '',
    },
    {
      Header: () => <Translation name="T.misc.quantity" />,
      className: 'text-right',
      value: (row) => row.openQuantity,
      Cell: ({ row }) => <>{row.openQuantity} / {row.quantity}</>
    },
    {
      Header: () => <Translation name="T.misc.shipdate" />,
      Cell: ({ row }) => <CDate format={DATE} val={row.shipDate} />,
      value: (row) => row.shipDate,
    },
  ],
});

const HistoryTable = beTableAsComponent<IItemMovementHistoryEntry>({
  tableId: 'itemHistoryTable',
  listLayoutBelow: 'md',
  columns: [
    {
      id: 'type',
      Header: () => <Translation name="T.objects.item.history.transType" />,
      value: (row) => `${row.transactionType} (${row.documentCode})`,
      filterable: true,
      fullText: true,
    },
    {
      id: 'at',
      Header: () => <Translation name="T.objects.item.history.at" />,
      Cell: ({ row }) => <CDate format={DATE_TIME} val={row.at} />,
      value: (row) => row.at,
      fullText: true,
    },
    {
      id: 'username',
      Header: () => <Translation name="T.objects.item.history.username" />,
      value: (row) => row.username,
      filterable: true,
    },
    {
      id: 'quantity',
      Header: () => <Translation name="T.objects.item.history.quantity" />,
      className: 'text-right',
      value: (row) => row.quantity,
    },
    {
      id: 'batchSerial',
      Header: () => <Translation name="T.objects.item.history.batchSerial" />,
      Cell: ({ row }) => {
        if (isBatch(row.item)) {
          return <Batch obj={row.batch} inline/>
        }
        if (isSerial(row.item)) {
          return <Serial obj={row.serialNumber} inline/>
        }
        return null
      },
      value: (row) => row.serialNumber?.visualCode ?? row.batch?.visualCode ?? "",
      filterable: true,
    },
    {
      id: 'bin',
      Header: () => <Translation name="T.objects.item.history.bin" />,
      Cell: ({ row }) => <Location obj={row.bin} inline />,
      value: (row) => row.bin.visualCode,
      filterable: true,
    },
  ],
});

interface Props {
  inline?: boolean;
  obj: IItem | null;
  highlightedBatch?: IBatch | null;
  highlightedSerial?: ISerialNumber | null;
}

type DefaultLocationFilter = 'present' | 'active' | 'all';

export const getShortDescription = (item: IItem) => <Translation name="T.objects.item.shortDescription" params={(item as any) || {}} />

const LOADING = {}
const Whole2part: FC<{ item: IItem }> = ({ item }) => {
  const rerender = useRerender();
  const api = useApi()
  const [partItem, setPartItem] = useState<IItem>(LOADING as any)

  useEffectAsync(async () => {
    if (!item.wholeToPartItem) {
      setPartItem(null)
    } else {
      setPartItem(LOADING as any)
      setPartItem(await api.item.getItem(item.wholeToPartItem))
    }
  }, [item.wholeToPartItem])

  const buttons = (lbStyle: any = undefined, rbStyle: any = undefined) => (
    <>
      <ProcessingButton
        color="secondary" 
        className='h-auto py-0' 
        style={lbStyle}
        onClick={async () => {
          if (await confirm(<Translation name="T.hints.areYouSure"/>)) {
            await api.item.whole2part.setPart(item.code, null, null)
            item.wholeToPartItem = null
            item.wholeToPartQty = null
            rerender()
          }
        }}
      >
        <MdClear/>
      </ProcessingButton>
      <ProcessingButton
        className='h-auto py-0'
        color="primary"
        style={rbStyle}
        onClick={async () => {
          const input = await promptAutocomplete(
            <Translation name="T.hints.item"/>, 
            '', 
            api.item.findAutocomplete
          )
          if (!input) {
            return;
          }

          const { item: partItem } = await compoundItemBarcodeParser(
            input, 
            codes => api.item.findMultipleItem(codes),
            'itemCode'
          )
          if (!partItem) {
            throw addNotification('danger', <Translation name="T.errors.itemIdentification.unknownItemForCode" params={{ code: input }} />);
          }
          
          const sqty = await prompt(
            <Translation name="T.hints.quantity"/>,
            '',
          )
          if (sqty === null) {
            return
          }

          const qty = parseFloat(sqty);
          if (isNaN(qty) || isNaN(Number(qty))) {
            return
          }

          await api.item.whole2part.setPart(item.code, partItem.code, qty)
          item.wholeToPartItem = partItem.code
          item.wholeToPartQty = qty
          rerender()
        }}
      >
        <FaEdit/>
      </ProcessingButton>
    </>
  )

  return (
    <>
      <InputGroup>
        <div className="form-control h-auto py-0">
          {partItem === LOADING ? (
            <Spinner size="sm" />
          ) : (
            (item.wholeToPartItem && item.wholeToPartQty) ? (
              <>{item.wholeToPartQty} <FaTimes/> <Item inline obj={partItem}/></>
            ) : <>&nbsp;</>
          )}
        </div>
        <InputGroupAddon addonType="append" className='d-none d-md-flex'>
          {buttons()}
        </InputGroupAddon>
      </InputGroup>
      <ButtonGroup className='w-100 d-md-none'>
        {buttons({ borderTopLeftRadius: 0 }, { borderTopRightRadius: 0 })}
      </ButtonGroup>
    </>
  )
}

const Item = ({ obj: item, inline, highlightedBatch, highlightedSerial }: Props) => {
  const api: IApi = useApi();
  const config = useConfig();
  const user = useAuth();
  const T = useTranslations();
  const deviceConfig = useDeviceConfig().deviceConfig;
  const specializations = useSpecializations()

  const [locations, setLocations] = useState<IItemBinLocation[]>(null)
  const rerender = useRerender();
  const {
    data: openDocuments,
    isLoading: openDocumentsLoading,
    reload: loadOpenDocuments,
  } = useApiLoader(
    useCallback((api) => api.item.getOpenDocuments(item.code), [item?.code]), // eslint-disable-line react-hooks/exhaustive-deps
    [],
    false,
  );
  const {
    data: history,
    isLoading: historyLoading,
    reload: loadHistory,
  } = useApiLoader(
    useCallback((api) => api.item.getHistory(item.code), [item?.code]), // eslint-disable-line react-hooks/exhaustive-deps
    [],
    false,
  );
  const [defaultLocations, setDefaultLocations] = useState<Array<IDefaultBinLocation>>(null);
  const [defaultLocationsFilter, setDefaultLocationsFilter] = useState<DefaultLocationFilter>('present');
  const [pdf, setPdf] = useState(null)
  const [imageKey, setImageKey] = useState(0)
  const reloadImage = () => setImageKey(old => old + 1)

  const locationTree = useMemo(() => {
    if (!locations) {
      return []
    }
    return toLocationTree(locations)
  }, [locations])

  const batches = useMemo(() => {
    if (!locations)
      return []
    return sortBy(
      values(groupBy(locations.filter(it => it.batch), it => it.batch.code)).map(it => {
        return {
          batch: it[0].batch,
          onHandQuantity: sumBy(it, it => it.onHandQty),
          locations: values(groupBy(it, it => it.binLocation.code)).map(locations => {
            return {
              location: locations[0].binLocation,
              onHandQuantity: sumBy(locations, it => it.onHandQty),
            }
          })
        }
      }),
      it => highlightedBatch && highlightedBatch.code === it.batch.code ? 0 : 1
    )
  }, [locations, highlightedBatch])

  const serials = useMemo(() => {
    if (!locations)
      return []
    return sortBy(
      locations.filter(it => it.serialNumber).map(it => ({
        serial: it.serialNumber,
        location: it.binLocation,
      })),
      it => highlightedSerial && highlightedSerial.code === it.serial.code ? 0 : 1
    )
  }, [locations, highlightedSerial])

  useEffect(() => {
    setLocations(null);
    setPdf(null);
  }, [item?.code]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLocations = async () => {
    setLocations(await api.item.getItemLocations(item.code));
  };

  const loadDefaultLocations = async () => {
    setDefaultLocations(await api.item.getDefaultLocations(item.code));
  };

  const shortDescription = () => getShortDescription(item);

  const printLabels = async (quantity: number) => {
    if (quantity) {
      await api.item.printLabel(
        await askPrinterIfNotSet(
          api,
          <Translation name="T.prompts.printerPicker.uses.itemLabels" />,
          deviceConfig.defaultItemLabelPrinter,
          'defaultItemLabelPrinter'
        ),
        {
          receiptCode: null,
          picklistCode: null,
          deliveryCode: null,
          productionCode: null,
          jobs: [{
            item,
            batch: null,
            serial: null,
            quantity
          }]
        }
      );
    }
  }

  const printLargeLabels = async (quantity: number) => {
    if (quantity) {
      await api.item.printLargeLabel(
        await askPrinterIfNotSet(
          api,
          <Translation name="T.prompts.printerPicker.uses.itemLargeLabels" />,
          deviceConfig.defaultPrinter,
          'defaultPrinter'
        ),
        {
          receiptCode: null,
          picklistCode: null,
          deliveryCode: null,
          productionCode: null,
          jobs: [{
            item,
            batch: null,
            serial: null,
            quantity
          }]
        }
      );
    }
  }

  const barcodeSetter = async (field: EBarcodeFields) => {
    const barcode = await prompt(<Translation name="T.hints.barcode"/>, '')
    if (barcode != null) {
      const setBarcode = async (b: string) => {
        const sqty = await prompt(<Translation name="T.hints.barcodeInvQty"/>, '1.0', true)

        if (sqty === null) {
          return
        }

        const qty = parseFloat(sqty);
        if (isNaN(qty) || isNaN(Number(qty))) {
          return
        }

        await api.item.setBarcode(item.code, field, b, qty);
        if (field === 'BARCODE1') {
          item.barcode = b
          item.barcodeInvQty = qty
        } else if (field === 'BARCODE2') {
          item.barcode2 = b
          item.barcode2InvQty = qty
        } else if (field === 'BARCODE3') {
          item.barcode3 = b
          item.barcode3InvQty = qty
        }
        addNotification('success', <Translation name="T.misc.success"/>)
        rerender()
      }

      // get the possible compounds from this barcode
      let compounds = await compiledUserCompoundItemBarcodeParser('itemCode', barcode)
      if (compounds.length === 0) {
        addNotification('danger', <Translation name="T.errors.noBarcodeFound"/>)
        return
      }

      if (compounds.length === 1) {
        setBarcode(compounds[0].itemCode)
        return
      }
    
      new Audio(serialSfx).play();
      const selected = await choiceList(
        <Translation name="T.objects.item.whatBarcodeToUse" params={{ code: barcode }}/>,
        compounds.map(it => ({
          value: it,
          label: (
            <Translation 
              name='T.objects.item.barcodeSelectionTile' 
              params={it}
            />
          )
        }))
      )
      if (selected) {
        setBarcode(selected.itemCode)
        return
      }
    }
  }

  const customTabs = useCustomTabs('items', item?.code)
  
  const detailedRender = () => {
    return (
      <UncontrolledTabs id='item-info-tabs' key={item?.code}>
        <Tab 
          dataTestId='itemInfoTabGeneral' 
          name={<Translation name="T.misc.information" />}
        >
          <Container fluid>
            <Row>
              <Col sm="12" md="12" lg="4" className="text-center">
                <ItemImage
                  key={imageKey}
                  obj={item}
                  openOnClick
                  imgProps={{
                    style: {
                      maxHeight: '400px',
                      maxWidth: '100%',
                      objectFit: 'contain',
                    },
                  }}
                />
              </Col>

              <Col sm="12" md="12" lg="8">
                <PropertyList
                  rows={[
                    [
                      <b>
                        <Translation name="T.misc.code"/>
                      </b>,
                      item.code
                    ],
                    [
                      <b>
                        <Translation name="T.objects.item.itemCode" />
                      </b>,
                      <>
                        {item!.visualCode}
                        {' '}
                        <RoleProtected roles={['authCopyObjectCodes']}>
                          <ProcessingButton color="link" className='p-0' onClick={async () => {
                            navigator.clipboard.writeText(item!.visualCode)
                            await sleep(100)        
                          }}>
                            <FaCopy/>
                          </ProcessingButton>
                        </RoleProtected>
                      </>,
                    ],
                    [
                      <b>
                        <Translation name="T.objects.item.itemName" />
                      </b>,
                      <>{item!.name}<br/>{item!.secondName}</>,
                    ],
                    [
                      <b>
                        <Translation name="T.objects.item.barcode1" />
                      </b>,
                      <>
                        {item!.barcode && `${item!.barcode} x ${item.barcodeInvQty} `}
                        <If cond={user.expandedRoles.includes('barcodeSetup')}>
                          <ProcessingButton className='py-0' data-testid='set-barcode-1' onClick={() => barcodeSetter('BARCODE1')}>
                            <FaEdit/>
                          </ProcessingButton>
                        </If>
                      </>,
                    ],
                    [
                      <b>
                        <Translation name="T.objects.item.barcode2" />
                      </b>,
                      <>
                        {item!.barcode2 && `${item!.barcode2} x ${item.barcode2InvQty} `}
                        <If cond={user.expandedRoles.includes('barcodeSetup')}>
                          <ProcessingButton className='py-0' data-testid='set-barcode-2' onClick={() => barcodeSetter('BARCODE2')}>
                            <FaEdit/>
                          </ProcessingButton>
                        </If>
                      </>,
                    ],
                    [
                      <b>
                        <Translation name="T.objects.item.barcode3" />
                      </b>,
                      <>
                        {item!.barcode3 && `${item!.barcode3} x ${item.barcode3InvQty} `}
                        <If cond={user.expandedRoles.includes('barcodeSetup')}>
                          <ProcessingButton className='py-0' data-testid='set-barcode-3' onClick={() => barcodeSetter('BARCODE3')}>
                            <FaEdit/>
                          </ProcessingButton>
                        </If>
                      </>,
                    ],
                    [
                      <b>
                        <Translation name="T.misc.units" />
                      </b>,
                      item!.units,
                    ],
                  ]}
                />
              </Col>
            </Row>
          </Container>
        </Tab>
        <Tab 
          dataTestId='itemInfoDetails' 
          name={<Translation name="T.misc.details" />}
        >
          <PropertyList
            rows={[
              [
                <b>
                  <Translation name="T.objects.item.groupname" />
                </b>,
                item!.groupName,
              ],
              [
                <b>
                  <Translation name="T.objects.item.managedBy" />
                </b>,
                item!.managedBy,
              ],
              item.managedBy === 'SERIAL_NUMBER' && [
                <b>
                  <Translation name="T.objects.item.serialManagedByDispatch" />
                </b>,
                <Bool val={item!.serialManagedByDispatch} type="YN" />,
              ],
              [
                <b>
                  <Translation name="T.objects.item.avgPrice" />
                </b>,
                item!.avgPrice,
              ],
              [
                <b>
                  <Translation name="T.objects.item.isActive" />
                </b>,
                <Bool val={item!.active} type="YN" />,
              ],
              [
                <b>
                  <Translation name="T.objects.item.isStockItem" />
                </b>,
                <Bool val={item!.isStockItem} type="YN" />,
              ],
              [
                <b>
                  <Translation name="T.objects.item.isSalesBomItem" />
                </b>,
                <Bool val={item!.isSalesBomItem} type="YN" />,
              ],
              [
                <b>
                  <Translation name="T.objects.item.isProdItem" />
                </b>,
                <Bool val={item!.isProductionItem} type="YN" />,
              ],
              [
                <b>
                  <Translation name="T.objects.item.weight" />
                </b>,
                <>{item!.weightInMg / 1e6} kg</>,
              ],
              [
                <b>{<Translation name="T.objects.item.whole2part" />}</b>, 
                <Whole2part key={item.code} item={item}/>
              ],
              [
                <b>
                  <Translation name="T.objects.item.productionMaterialTolerance" />
                </b>,
                <>
                  {item.productionMaterialToleranceValue} {item.productionMaterialToleranceType}
                </>,
              ],
              hasBatch(item) && [
                <b>
                  <Translation name="T.objects.item.blockForQcType" />
                </b>,
                item.blockForQcType,
              ],
              item.hasBatch && [
                <b>
                  <Translation name="T.objects.item.receiptScanExpirationDate" />
                </b>,
                <Bool type='YN' val={item.receiptScanPreferences?.scanExpirationDate}/>,
              ],
              item.hasBatch && [
                <b>
                  <Translation name="T.objects.item.receiptScanManufacturingDate" />
                </b>,
                <Bool type='YN' val={item.receiptScanPreferences?.scanManufacturingDate}/>,
              ],
              item.hasBatch && [
                <b>
                  <Translation name="T.objects.item.receiptScanManufacturerBatch" />
                </b>,
                <Bool type='YN' val={item.receiptScanPreferences?.scanManufacturingBatch}/>,
              ],
              !item.serialManagedByDispatch && item.serial && [
                <b>
                  <Translation name="T.objects.item.receiptScanManufacturerserial" />
                </b>,
                <Bool type='YN' val={item.receiptScanPreferences?.scanManufacturingSerial}/>,
              ],
            ]}
          />

          <If cond={item?.pdfPath}>
            <ProcessingButton block onClick={async () => {
              const req = await fetch(item.pdfPath)
              const blob = await req.blob()
              const data = await new Promise(resolve => {
                var reader = new FileReader();
                reader.onload = function() { 
                  return resolve((this.result as string).split(',')[1])
                };
                reader.readAsDataURL(blob);
              })
              setPdf(data)
            }}>
              <Translation name="T.objects.item.showPdfFile" />
            </ProcessingButton>
            <PdfPreview pdf={pdf} setPdf={setPdf}/>
          </If>

          {run(() => {
            const C = specializations?.augmentations?.objects?.item?.mainTab
            if (!C) {
              return null
            }
            return <C item={item}/>
          })}
        </Tab>
        <Tab 
          dataTestId='locations'
          name={<Translation name="T.objects.item.locations" />}
          onFirstOpen={loadLocations}
          hidden={config.settings.systemOperationMode === 'WALL_TO_WALL_BATCHING_MODE' && !user.expandedRoles.includes('admin')}
        >
          <LocationsTable
            data={locationTree}
            loading={locations === null}
          />
        </Tab>
        <Tab 
          name={<Translation name="T.objects.item.activeBatches" />}
          onFirstOpen={loadLocations}
          hidden={item?.managedBy !== 'BATCH_NUMBER'}
        >
          <BatchesTable 
            data={batches}
            loading={locations === null}
            getExtraProps={() => ({ highlightedBatch })}
          />
        </Tab>
        <Tab 
          name={<Translation name="T.objects.item.activeSerials" />}
          onFirstOpen={loadLocations}
          hidden={item?.managedBy !== 'SERIAL_NUMBER'}
        >
          <SerialsTable
            data={serials}
            loading={locations === null}
            getExtraProps={() => ({ highlightedSerial })}
          />
        </Tab>
        <Tab 
          name={<Translation name="T.objects.item.defaultLocationsTabHeader" />}
          onFirstOpen={loadDefaultLocations}
        >
          <>
            <Input
              type="select"
              value={defaultLocationsFilter}
              onChange={(e) => setDefaultLocationsFilter(e.target.value as DefaultLocationFilter)}
            >
              <option value="present">{T('T.misc.onlyPresent')}</option>
              <option value="active">{T('T.misc.onlyActive')}</option>
              <option value="all">{T('T.misc.all')}</option>
            </Input>
            <DefaultLocationsTable
              data={(defaultLocations || []).filter((it) => {
                if (defaultLocationsFilter === 'present') {
                  return it.binLocation;
                }
                if (defaultLocationsFilter === 'active') {
                  return it.whs.active;
                }
                return true;
              })}
              colShowProps={{ auth: user }}
              getExtraProps={() => ({
                item,
                loadDefaultLocations,
              })}
            />
          </>
        </Tab>
        <Tab 
          name={<Translation name="T.misc.openDocuments" />}
          onFirstOpen={loadOpenDocuments}
        >
          <OpenDocumentsTable loading={openDocumentsLoading} data={openDocuments} getExtraProps={() => ({ item: item })} />
        </Tab>
        <Tab 
          dataTestId='history'
          name={<Translation name="T.misc.history" />}
          onFirstOpen={loadHistory}
        >
          <HistoryTable loading={historyLoading} data={history} />
        </Tab>
        <Tab 
          dataTestId='actions'
          name={<Translation name="T.misc.actions" />}
        >
          <>
            <ButtonGroup className="w-100">
              <ProcessingButton
                block
                data-testid='print-labels'
                color="primary"
                onClick={async () => await printLabels(
                  parseInt(await prompt(<Translation name="T.misc.howManyLabels" />, ``))
                )}
              >
                <Translation name="T.misc.printLabels" />
              </ProcessingButton>
              <ProcessingButton
                data-testid="print-one-label"
                color="info"
                onClick={async () => await printLabels(1)}
              >
                <RiNumber1/>
              </ProcessingButton>
            </ButtonGroup>
            <br/>

            <ButtonGroup className="w-100">
              <ProcessingButton
                block
                color="primary"
                onClick={async () => await printLargeLabels(
                  parseInt(await prompt(<Translation name="T.misc.howManyLabels" />, ``))
                )}
              >
                <Translation name="T.misc.printLargeLabels" />
              </ProcessingButton>
              <ProcessingButton
                color="info"
                onClick={async () => await printLargeLabels(1)}
              >
                <RiNumber1/>
              </ProcessingButton>
            </ButtonGroup>

            <RoleProtected roles={['itemManagedByEdit']}>
              <br/>
              <UpdateManagedBy item={item}/>
            </RoleProtected>

            <br/>
            <ItemImageSetter
              itemCode={item.code}
              onImageChanged={it => {
                item.imagePath = it;
                reloadImage()
              }}
            />

            <RoleProtected roles={['itemAuthCreateProductionOrder']}>
              <br/>
              <ProductionOrderCreator
                trigger={(open: any) => (
                  <ProcessingButton
                    block
                    color="primary"
                    onClick={open}
                  >
                    <Translation name="T.objects.item.createProductionOrderButton" />
                  </ProcessingButton>
                )}
                item={item}
                targetQuantity={1.0}
              />
            </RoleProtected>
          </>

          {run(() => {
            const C = specializations?.augmentations?.objects?.item?.actionsTab
            if (!C) {
              return null
            }
            return <C item={item}/>
          })}
        </Tab>
        {customTabs}
      </UncontrolledTabs>
    );
  };

  const dialog = {
    titleText: <Translation name="T.objects.item.itemDetails" />,
    closeBtnText: <Translation name="T.misc.close" />,
    content: () => <Item obj={item} />,
  };

  return (
    <Model
      inline={inline}
      hasLoaded={item}
      copyValue={() => item.visualCode}
      description={shortDescription}
      detailedRender={detailedRender}
      dialog={dialog}
    />
  );
};

export const ItemLocationsTable = LocationsTable;
export default Item;
